import { FC, ReactNode, SyntheticEvent } from "react";

import { Accordion, AccordionDetails, SxProps } from "@mui/material";

import { AccordionDescription, AccordionTitle, StyledAccordionSummary } from "@/components/Accordion/components";

interface IStyledAccordion {
  expanded: string | false;
  title: string | ReactNode;
  description: string | ReactNode;
  panel: string;
  handleChange: (panel: string) => (event: SyntheticEvent, newExpanded: boolean) => void;
  sx?: SxProps;
  accordionSx?: SxProps;
  withoutMargin?: boolean;
  /** TEMP review aid — renders an amber "needs review" dot before the title. */
  needsReview?: boolean;
}

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const StyledAccordion: FC<IStyledAccordion> = ({
  description,
  title,
  panel,
  expanded,
  handleChange,
  sx,
  accordionSx,
  needsReview,
}) => {
  return (
    <Accordion
      expanded={expanded === panel}
      onChange={handleChange(panel)}
      // MUI's default Accordion adds margin: 16px 0 when .Mui-expanded
      // is applied — that pushes siblings up/down on each expand+collapse
      // and looks like the section / background shifts. Zero the margin
      // so expand just grows the panel in-place without nudging anything
      // else. The :before pseudo-element (the divider line MUI inserts
      // between accordions) is left alone — it stays visible at the top
      // of each accordion regardless of expanded state.
      sx={{
        "&.Mui-expanded": { margin: 0 },
        "&.Mui-expanded:first-of-type": { marginTop: 0 },
        "&.Mui-expanded:last-of-type": { marginBottom: 0 },
        ...(accordionSx as object),
      }}
    >
      <StyledAccordionSummary
        expandIcon={<ChevronIcon />}
        aria-controls={`${panel}a-content`}
        id={`${panel}a-header`}
      >
        <AccordionTitle>
          {needsReview && (
            <span
              title="Needs content review"
              style={{
                display: "inline-block",
                width: "9px",
                height: "9px",
                borderRadius: "50%",
                background: "#f5a623",
                boxShadow: "0 0 6px rgba(245, 166, 35, 0.7)",
                marginRight: "9px",
                verticalAlign: "middle",
                flexShrink: 0,
              }}
            />
          )}
          {title}
        </AccordionTitle>
      </StyledAccordionSummary>

      <AccordionDetails>
        {/* component="div" so multi-paragraph / list descriptions are valid (not nested in a <p>) */}
        <AccordionDescription {...({ component: "div" } as object)} sx={sx}>{description}</AccordionDescription>
      </AccordionDetails>
    </Accordion>
  );
};

export default StyledAccordion;
